import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Section } from "@/components/kit";
import { brl } from "@/lib/api";

export function implicitRate(pv, pmt, n) {
  if (!(pv > 0 && pmt > 0 && n > 0) || pmt * n <= pv) return 0;
  let lo = 0, hi = 1;
  for (let i = 0; i < 100; i++) {
    const r = (lo + hi) / 2;
    const v = (pmt * (1 - (1 + r) ** -n)) / r;
    if (v > pv) lo = r; else hi = r;
  }
  return ((lo + hi) / 2) * 100;
}

const fmtPct = (v) => `${v.toFixed(2).replace(".", ",")}%`;

function Row({ d, invest }) {
  const total = d.min_payment * d.remaining_installments;
  const disc = total - d.payoff_amount;
  const rate = implicitRate(d.payoff_amount, d.min_payment, d.remaining_installments);
  const worth = rate > invest;
  return (
    <div className="rounded-xl border border-[#1E3A6E] bg-[#0B1A33] p-4" data-testid={`payoff-${d.id}`}>
      <div className="font-semibold">{d.name}</div>
      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
        <div><div className="text-xs text-slate-400">Pagando as {d.remaining_installments} parcelas</div><div className="num text-red-300" data-testid={`payoff-total-${d.id}`}>{brl(total)}</div></div>
        <div><div className="text-xs text-slate-400">Quitando hoje</div><div className="num text-[#F5D77F]">{brl(d.payoff_amount)}</div></div>
        <div><div className="text-xs text-slate-400">Desconto na quitação</div><div className="num text-emerald-300" data-testid={`payoff-discount-${d.id}`}>{brl(disc)} · {fmtPct(total ? (disc / total) * 100 : 0)}</div></div>
        <div><div className="text-xs text-slate-400">Juros embutidos do contrato</div><div className="num">{fmtPct(rate)} a.m.</div></div>
      </div>
      <p className={`mt-3 rounded-lg p-2 text-xs ${worth ? "bg-emerald-500/10 text-emerald-300" : "bg-sky-500/10 text-sky-200"}`} data-testid={`payoff-verdict-${d.id}`}>
        {worth ? `Quitar tende a compensar: o contrato cobra ${fmtPct(rate)} a.m., mais que o rendimento de ${fmtPct(invest)} a.m. Use apenas recursos fora da reserva de emergência.`
          : `Manter as parcelas e investir o dinheiro tende a render mais (${fmtPct(invest)} a.m. contra ${fmtPct(rate)} a.m. do contrato).`}
      </p>
    </div>
  );
}

export function PayoffCompare({ debts }) {
  const [invest, setInvest] = useState(0.85);
  const ready = debts.filter((d) => d.remaining_installments > 0 && d.payoff_amount > 0 && d.min_payment > 0);
  const total = ready.reduce((a, d) => a + d.min_payment * d.remaining_installments, 0);
  const payoff = ready.reduce((a, d) => a + d.payoff_amount, 0);
  return (
    <Section title="Comparativo de quitação antecipada" className="mb-6" testid="payoff-compare">
      <div className="mb-4 flex flex-wrap items-end gap-4 text-sm">
        <div><label className="text-xs text-slate-400">Rendimento do seu dinheiro (% ao mês)</label>
          <Input type="number" step="0.01" value={invest} onChange={(e) => setInvest(Number(e.target.value))} className="mt-1 w-40 bg-[#0B1A33]" data-testid="payoff-invest-rate" /></div>
        {ready.length > 0 && <div className="text-slate-300" data-testid="payoff-summary">Total restante <b className="num text-red-300">{brl(total)}</b> · quitação <b className="num text-[#F5D77F]">{brl(payoff)}</b> · economia <b className="num text-emerald-300">{brl(total - payoff)}</b></div>}
      </div>
      {ready.length ? <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{ready.map((d) => <Row key={d.id} d={d} invest={invest} />)}</div>
        : <p className="text-sm text-slate-400">Edite uma dívida e preencha "Parcelas restantes" e "Valor para quitação hoje" (informado pelo banco) para ver o comparativo.</p>}
    </Section>
  );
}
